import { useId } from 'react';
import { palette } from '../../styles/tokens';

/**
 * PercentInput — typed percentage input primitive.
 *
 * Emits clamped numeric value [min, max] via onChange. Empty input emits 0.
 * Displays a trailing % suffix and renders an optional error message below.
 *
 * Mirrors MoneyInput's prefix/suffix wrapper pattern (absolutely positioned
 * span + extra right padding on the input). Inline style approach matches
 * admin convention; no Tailwind, no design-token additions.
 */

export interface PercentInputProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  error?: string;
  ariaLabel?: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

const WRAPPER: React.CSSProperties = {
  position: 'relative',
  minWidth: 0,
};

const SUFFIX: React.CSSProperties = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 13.5,
  color: palette.g400,
  pointerEvents: 'none',
  userSelect: 'none',
};

const INPUT_BASE: React.CSSProperties = {
  width: '100%',
  padding: '9px 24px 9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
  textAlign: 'right' as const,
};

const INPUT_ERROR: React.CSSProperties = {
  borderColor: palette.red,
};

const INPUT_DISABLED: React.CSSProperties = {
  background: palette.g50,
  color: palette.g400,
  cursor: 'not-allowed',
};

const ERROR_TEXT: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: palette.red,
};

export function PercentInput({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  error,
  ariaLabel,
  id: idProp,
  className,
  style,
}: PercentInputProps) {
  const reactId = useId();
  const id = idProp ?? reactId;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(0);
      return;
    }
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.max(min, Math.min(max, parsed));
    onChange(clamped);
  };

  const inputStyle: React.CSSProperties = {
    ...INPUT_BASE,
    ...(error ? INPUT_ERROR : {}),
    ...(disabled ? INPUT_DISABLED : {}),
  };

  return (
    <div className={className} style={{ ...WRAPPER, ...style }}>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={Number.isFinite(value) ? value : ''}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        style={inputStyle}
      />
      <span style={SUFFIX}>%</span>
      {error && <div id={`${id}-error`} style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
